// The waste question's one vocabulary: the five readings a site-visit can
// carry, in report order, with the colours the map and the split bar share.
//
// The five partition every visit: treated-found and treated-none are the
// treated half, checked-none, no-record and found-untreated are the visits no
// intervention touched. The colours are not decorative — the stacked bar, the
// legend and the outcome table are the same five keys, so a reader who learns
// the swatch once reads every panel.
import type { MapReviewWasteTotals } from "@/type/api";

import { formatCount } from "../charts/theme";

/** The outcome fields of the totals. `visits` is the whole and `treated` is a
 * rolled-up pair, so neither is a row of the split. */
export type WasteOutcomeKey = Exclude<
	keyof MapReviewWasteTotals,
	"visits" | "treated"
>;

export interface WasteOutcome {
	key: WasteOutcomeKey;
	label: string;
	color: string;
	hint: string;
}

/** The five outcomes, in the order the report states them. */
export const WASTE_OUTCOMES: readonly WasteOutcome[] = [
	{
		key: "treatedFound",
		label: "Treated after finding production",
		color: "#0072b2",
		hint: "the visit found production and applied an intervention",
	},
	{
		key: "treatedNone",
		label: "Treated without a finding",
		color: "#56b4e9",
		hint: "an intervention was applied with no positive inspection recorded",
	},
	{
		key: "checkedNone",
		label: "Checked, found none, did nothing",
		color: "#8a9098",
		hint: "an inspection ran and found no production; no intervention was needed",
	},
	{
		key: "noRecord",
		label: "No inspection, no intervention",
		color: "#e69f00",
		hint: "the visit recorded neither a check nor a treatment",
	},
	{
		key: "foundUntreated",
		label: "Found production, did nothing",
		color: "#d55e00",
		hint: "production was recorded and nothing was applied — the failure case",
	},
];

/** One outcome's visit count, by the totals field the outcome is keyed on. */
export function wasteTotal(
	totals: MapReviewWasteTotals,
	key: WasteOutcomeKey,
): number {
	return totals[key];
}

/** `part` as a percentage of `whole`. A missing denominator is zero rather than
 * an infinity the panel would have to special-case. */
export function wasteShare(part: number, whole: number): number {
	return whole === 0 ? 0 : (100 * part) / whole;
}

export { formatCount };
